/**
 * Roturas y detalles (spec 06; respuestas de la dueña del 2026-10-15).
 * - Rotura de vidrio: quien la ve elige la lámina, adjunta la foto y declara las piezas buenas en milímetros con
 *   cuántas («1 de 1400x3660, 2 de 1000x2500»). Tienen que caber en la lámina. La dueña arma las piezas para la venta
 *   (nombre, cantidad y precio) y aprueba: la lámina sale del inventario y las piezas entran a «PIEZAS DE VIDRIOS».
 * - Producto con detalle: el producto, cuántos, el detalle y la foto. La dueña pone el precio especial y aprueba: esas
 *   unidades salen de la existencia normal y pasan a «PRODUCTOS CON DETALLES».
 * Reporta cualquiera con acceso al inventario (también el cajero); aprueba y decide lo que no se vende solo la dueña.
 */
import {
  FOTOS_REPORTE_MAX,
  ARTICULO_PIEZAS_VIDRIO,
  ARTICULO_PRODUCTOS_DETALLE,
  areaM2,
  despieceVidrioInput,
  formatearCantidad,
  m2,
  medidaMm,
  Money,
  productoDetalleInput,
  revisarPiezasEnLamina,
  TIPO_SALIDA_PIEZA_ETIQUETA,
  type AlmacenDTO,
  type DespieceVidrioDTO,
  type EstadoPieza,
  type PiezaDTO,
  type ProductoResumenDTO,
  type SucursalDTO,
  type TipoSalidaPieza,
} from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { Camera, Layers, PackageX, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useTemaAyuda } from '@/ayuda/contexto.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { PaginadorBusqueda, usePaginaBusqueda } from '@/componentes/PaginadorBusqueda.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select, Textarea } from '@/componentes/ui/input.tsx';
import { Campo, Filtro } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { reducirFoto } from '@/lib/foto.ts';
import { normalizarDecimal } from '@/lib/montos.ts';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { useDespiecesVidrio, useEditarPieza, useFotoReporte, usePiezas, useRegistrarDespieceVidrio, useRegistrarProductoDetalle, useResolverDespieceVidrio, useSalidaPieza } from './api.ts';

const cant = (v: string) => formatearCantidad(Money.parse(v));
const usd = (v: string) => `$ ${Money.parse(v).toDisplay()}`;
type Variante = 'ok' | 'pendiente' | 'neutro' | 'critico' | 'info';
const ESTADO_PIEZA: Record<EstadoPieza, { texto: string; variante: Variante }> = {
  por_aprobar: { texto: 'Por aprobar', variante: 'pendiente' },
  disponible: { texto: 'En la lista', variante: 'ok' },
  agotada: { texto: 'Agotada', variante: 'info' },
  merma: { texto: 'Merma', variante: 'neutro' },
  rechazada: { texto: 'Rechazada', variante: 'neutro' },
};
const ESTADO_REPORTE: Record<DespieceVidrioDTO['estado'], { texto: string; variante: Variante }> = {
  por_aprobar: { texto: 'Por aprobar', variante: 'pendiente' },
  aprobado: { texto: 'Aprobado', variante: 'ok' },
  rechazado: { texto: 'Rechazado', variante: 'neutro' },
};
type SucursalMia = SucursalDTO & { almacenes: AlmacenDTO[] };
type Pestana = 'reportes' | 'vidrio' | 'detalle';
const TEMA_PESTANA: Record<Pestana, string> = { reportes: 'roturas.reportes', vidrio: 'roturas.piezas', detalle: 'roturas.detalles' };

export function Roturas() {
  const { puede } = useAuth();
  const puedeAprobar = puede('inventario.ajustes');
  const mia = useQuery({ queryKey: claves.sucursalMia, queryFn: () => get<SucursalMia | null>('/sucursales/mia') });
  const almacenes = mia.data?.almacenes ?? [];
  const [pestana, setPestana] = useState<Pestana>('reportes');
  useTemaAyuda(TEMA_PESTANA[pestana]);
  const [reportando, setReportando] = useState<'vidrio' | 'detalle' | null>(null);
  const boton = (p: Pestana, texto: string, testid: string) => (
    <button className={`px-4 py-1.5 text-sm font-medium ${pestana === p ? 'bg-mg-verde text-white' : 'bg-card'}`} onClick={() => setPestana(p)} data-testid={testid}>
      {texto}
    </button>
  );

  return (
    <div data-testid="roturas">
      <TituloPagina
        titulo="Roturas y detalles"
        subtitulo={puedeAprobar ? 'Revisa los reportes, pon los precios y decide qué pasa con lo que no se vende.' : 'Reporta un vidrio partido o un producto con detalle. La dueña lo aprueba y le pone el precio.'}
        acciones={
          almacenes.length > 0 ? (
            <>
              <Button variant="outline" onClick={() => setReportando('vidrio')} data-testid="btn-reportar-vidrio"><Layers /> Reportar rotura de vidrio</Button>
              <Button variant="outline" onClick={() => setReportando('detalle')} data-testid="btn-reportar-detalle"><PackageX /> Reportar producto con detalle</Button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">Toma contexto de una tienda para reportar.</span>
          )
        }
      />
      <div className="mb-3 inline-flex overflow-hidden rounded-lg border">
        {boton('reportes', 'Reportes', 'tab-reportes')}
        {boton('vidrio', 'Piezas de vidrio', 'tab-piezas')}
        {boton('detalle', 'Productos con detalles', 'tab-detalles')}
      </div>
      {pestana === 'reportes' ? <Reportes puedeAprobar={puedeAprobar} /> : <Lotes key={pestana} clase={pestana} />}
      {reportando === 'vidrio' ? <ReportarRotura almacenes={almacenes} cerrar={() => setReportando(null)} /> : null}
      {reportando === 'detalle' ? <ReportarDetalle almacenes={almacenes} cerrar={() => setReportando(null)} /> : null}
    </div>
  );
}

// ─── Reportes ───────────────────────────────────────────────────────────────
function Reportes({ puedeAprobar }: { puedeAprobar: boolean }) {
  const reportes = useDespiecesVidrio(true);
  const [revisando, setRevisando] = useState<DespieceVidrioDTO | null>(null);
  return (
    <>
      <EstadoQuery cargando={reportes.isLoading} error={reportes.error} datos={reportes.data} reintentar={() => void reportes.refetch()}>
        {(d) =>
          d.length === 0 ? (
            <Vacio icono={<Layers className="h-6 w-6" />} titulo="Sin reportes" descripcion="Cuando se parta una lámina o un producto tenga un detalle, repórtalo con las fotos." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reporte</TableHead>
                  <TableHead>Qué</TableHead>
                  <TableHead className="text-right">Cant.</TableHead>
                  <TableHead>Almacén</TableHead>
                  <TableHead>Reportó</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.map((x) => (
                  <TableRow key={x.id} data-testid={`reporte-${x.numero_formateado}`}>
                    <TableCell>
                      <p className="font-medium">{x.numero_formateado}</p>
                      <p className="text-[11px] text-muted-foreground">{x.clase === 'vidrio' ? 'Rotura de vidrio' : 'Producto con detalle'}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{x.producto_nombre}</p>
                      <p className="max-w-72 truncate text-[11px] text-muted-foreground" title={x.motivo}>
                        {x.clase === 'vidrio' ? `${x.piezas.length} medida(s) · ` : ''}
                        {x.motivo}
                      </p>
                    </TableCell>
                    <TableCell className="num text-right">{x.clase === 'vidrio' ? `${cant(x.cantidad)} lám.` : cant(x.cantidad)}</TableCell>
                    <TableCell>{x.almacen_nombre}</TableCell>
                    <TableCell className="text-xs">{x.creado_por_nombre}<br />{formatearFechaHora(x.creado_en)}</TableCell>
                    <TableCell><Badge variant={ESTADO_REPORTE[x.estado].variante}>{ESTADO_REPORTE[x.estado].texto}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant={x.estado === 'por_aprobar' && puedeAprobar ? 'default' : 'ghost'} onClick={() => setRevisando(x)} data-testid={`revisar-${x.numero_formateado}`}>
                        {x.estado === 'por_aprobar' && puedeAprobar ? 'Revisar' : 'Ver'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </EstadoQuery>
      {revisando ? <RevisarReporte key={revisando.id} reporte={revisando} puedeAprobar={puedeAprobar} cerrar={() => setRevisando(null)} /> : null}
    </>
  );
}

/**
 * Fotos de la prueba (hasta 5, respuesta de la dueña del 26-10-2026): se toman con la cámara del teléfono o se eligen,
 * varias a la vez o una por una; cada una se reduce antes de enviarla y se puede quitar.
 */
function FotosPrueba({ fotos, cambiar, testid }: { fotos: string[]; cambiar: (f: string[]) => void; testid: string }) {
  const [leyendo, setLeyendo] = useState(false);
  const caben = FOTOS_REPORTE_MAX - fotos.length;
  return (
    <Campo etiqueta={`Fotos de lo que pasó (hasta ${FOTOS_REPORTE_MAX})`} htmlFor={testid}>
      <div className="flex flex-wrap items-center gap-2">
        {fotos.map((f, i) => (
          <div key={i} className="relative">
            <img src={f} alt={`Foto ${i + 1} del reporte`} className="h-16 w-16 rounded-lg border object-cover" data-testid={`${testid}-vista`} />
            <button type="button" className="absolute -right-1.5 -top-1.5 rounded-full border bg-card p-0.5 shadow-sm" onClick={() => cambiar(fotos.filter((_, j) => j !== i))} aria-label={`Quitar la foto ${i + 1}`} data-testid={`${testid}-quitar-${i}`}>
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {fotos.length === 0 ? <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed text-muted-foreground"><Camera className="h-5 w-5" /></div> : null}
        {caben > 0 ? (
          <Input
            id={testid}
            type="file"
            accept="image/jpeg,image/png,image/*"
            multiple
            className="min-w-48 flex-1"
            disabled={leyendo}
            data-testid={testid}
            onChange={(e) => {
              const elegidas = [...(e.target.files ?? [])];
              e.target.value = '';
              if (elegidas.length === 0) return;
              if (elegidas.length > caben) toast.warning(`Se agregan ${caben} de ${elegidas.length}: el máximo es ${FOTOS_REPORTE_MAX} fotos por reporte`);
              setLeyendo(true);
              Promise.all(elegidas.slice(0, caben).map((a) => reducirFoto(a)))
                .then((nuevas) => cambiar([...fotos, ...nuevas].slice(0, FOTOS_REPORTE_MAX)))
                .catch((err: unknown) => toast.error('No se pudo usar la foto', { description: err instanceof Error ? err.message : String(err) }))
                .finally(() => setLeyendo(false));
            }}
          />
        ) : (
          <span className="text-xs text-muted-foreground" data-testid={`${testid}-lleno`}>Ya tiene {FOTOS_REPORTE_MAX} fotos: quita una para cambiarla.</span>
        )}
      </div>
    </Campo>
  );
}

/** Productos por página en los buscadores de los reportes (paginador debajo de la lista, pedido de la dueña 2026-10-20). */
const LIMITE_REPORTE = 50;
const useProductos = (q: string, pagina: number) =>
  useQuery({ queryKey: ['productos', 'para-reporte', q, pagina], queryFn: () => get<{ datos: ProductoResumenDTO[]; total: number }>('/productos', { q: q || undefined, activo: 'true', pagina, limite: LIMITE_REPORTE }), placeholderData: (p) => p });
/** Lo elegido sigue en la lista aunque se pase de página o se busque otra cosa. */
const conElegido = (opciones: ProductoResumenDTO[], elegido: ProductoResumenDTO | null) => (elegido && !opciones.some((p) => p.id === elegido.id) ? [elegido, ...opciones] : opciones);

interface FilaPieza {
  alto: string;
  ancho: string;
  cantidad: string;
  descripcion: string;
}
const FILA_VACIA: FilaPieza = { alto: '', ancho: '', cantidad: '1', descripcion: '' };

function ReportarRotura({ almacenes, cerrar }: { almacenes: AlmacenDTO[]; cerrar: () => void }) {
  const [almacenId, setAlmacenId] = useState(() => almacenes.find((a) => a.principal)?.id ?? almacenes[0]?.id ?? '');
  const [q, setQ] = useState('');
  const [productoId, setProductoId] = useState('');
  const [laminas, setLaminas] = useState('1');
  const [motivo, setMotivo] = useState('');
  const [fotos, setFotos] = useState<string[]>([]);
  const [filas, setFilas] = useState<FilaPieza[]>([{ ...FILA_VACIA }]);
  const [pagina, setPagina] = usePaginaBusqueda(q);
  const productos = useProductos(q, pagina);
  const [elegida, setElegida] = useState<ProductoResumenDTO | null>(null);
  const registrar = useRegistrarDespieceVidrio();
  // Solo las láminas con su medida en la ficha: con ella se revisa que las piezas quepan
  const laminasCatalogo = conElegido((productos.data?.datos ?? []).filter((p) => p.tipo === 'producto' && p.lamina_largo_mm && p.lamina_ancho_mm), elegida);
  const lamina = laminasCatalogo.find((p) => p.id === productoId) ?? null;
  const cambiar = (i: number, campo: keyof FilaPieza, valor: string) => setFilas((f) => f.map((x, j) => (j === i ? { ...x, [campo]: valor } : x)));
  // Las filas en blanco no cuentan: una lámina puede perderse entera
  const piezas = filas
    .filter((f) => f.alto.trim() !== '' || f.ancho.trim() !== '')
    .map((f) => ({ alto_mm: normalizarDecimal(f.alto), ancho_mm: normalizarDecimal(f.ancho), cantidad: f.cantidad.trim() || '1', ...(f.descripcion.trim() ? { descripcion: f.descripcion.trim() } : {}) }));
  const medibles = piezas.filter((z) => Number(z.alto_mm) > 0 && Number(z.ancho_mm) > 0 && /^\d+$/.test(z.cantidad));
  const revision = lamina && medibles.length === piezas.length && Number(normalizarDecimal(laminas)) > 0 ? revisarPiezasEnLamina(medibles, { largo_mm: lamina.lamina_largo_mm!, ancho_mm: lamina.lamina_ancho_mm! }, normalizarDecimal(laminas)) : null;
  // Qué fila es cada pieza (las filas en blanco no son piezas), para marcar en rojo la que no cabe
  const filaDePieza = filas.flatMap((f, i) => (f.alto.trim() !== '' || f.ancho.trim() !== '' ? [i] : []));
  const noCabe = (i: number) => !!revision && revision.no_caben.some((k) => filaDePieza[k] === i);
  const corrige = !!revision && (revision.excede || revision.no_caben.length > 0);

  const guardar = () => {
    const r = despieceVidrioInput.safeParse({ almacen_id: almacenId, producto_id: productoId, cantidad: normalizarDecimal(laminas), motivo, ...(fotos.length > 0 ? { fotos } : {}), piezas });
    if (!r.success) {
      const campo = r.error.issues[0]?.path[0];
      toast.error(campo === 'producto_id' ? 'Elige la lámina que se partió' : campo === 'almacen_id' ? 'Elige el almacén' : campo === 'piezas' ? 'Revisa las piezas: medida en milímetros y cuántas' : (r.error.issues[0]?.message ?? 'Revisa los datos'));
      return;
    }
    registrar.mutate(r.data, {
      onSuccess: (d) => {
        toast.success(`Rotura ${d.numero_formateado} reportada`, { description: 'Queda por aprobar: la dueña arma las piezas y les pone precio. La lámina sale del inventario cuando lo apruebe.' });
        cerrar();
      },
      onError: (e) => toast.error('No se pudo reportar', { description: mensajeDeError(e) }),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="dialogo-rotura">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Reportar rotura de vidrio</DialogTitle><AyudaVentana tema="roturas.rotura-vidrio" /></div>
          <DialogDescription>Elige la lámina que se partió, adjunta la foto y declara las piezas buenas que salieron, en milímetros. Lo demás queda como merma.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-3">
          <Campo etiqueta="Almacén" htmlFor="rv-almacen" className="col-span-3">
            <Select id="rv-almacen" value={almacenId} onChange={(e) => setAlmacenId(e.target.value)} data-testid="rv-almacen">
              {almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </Select>
          </Campo>
          <Campo etiqueta="Láminas" htmlFor="rv-cantidad" ayuda="1 · 0,5 …">
            <Input id="rv-cantidad" inputMode="decimal" value={laminas} onChange={(e) => setLaminas(e.target.value)} className="text-right" data-testid="rv-cantidad" />
          </Campo>
          <Campo etiqueta="Lámina que se partió" htmlFor="rv-lamina" className="col-span-4">
            <div className="flex gap-2">
              <Input placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} className="w-40" data-testid="rv-lamina-buscar" />
              <Select id="rv-lamina" value={productoId} onChange={(e) => { setProductoId(e.target.value); setElegida(laminasCatalogo.find((p) => p.id === e.target.value) ?? null); }} data-testid="rv-lamina">
                <option value="">Elige la lámina…</option>
                {laminasCatalogo.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
              </Select>
            </div>
            <PaginadorBusqueda pagina={pagina} limite={LIMITE_REPORTE} total={productos.data?.total ?? 0} onPagina={setPagina} testid="rv-lamina" className="mt-1 rounded-lg border" />
            {lamina ? (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="rv-lamina-medida">
                Medida de la lámina: {medidaMm(String(lamina.lamina_largo_mm), String(lamina.lamina_ancho_mm))} mm{lamina.lamina_espesor_mm ? ` · ${cant(lamina.lamina_espesor_mm)} mm de espesor` : ''} ({m2(areaM2(String(lamina.lamina_largo_mm), String(lamina.lamina_ancho_mm)))})
              </p>
            ) : productos.data && laminasCatalogo.length === 0 ? (
              <p className="mt-1 text-xs text-mg-ambar">Ninguna lámina con esa búsqueda tiene su medida en la ficha. Pídele a la dueña que la ponga en Productos.</p>
            ) : null}
          </Campo>
        </div>
        <Campo etiqueta="Qué pasó" htmlFor="rv-motivo">
          <Textarea id="rv-motivo" rows={2} placeholder="Se partió al descargar el camión" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="rv-motivo" />
        </Campo>
        <FotosPrueba fotos={fotos} cambiar={setFotos} testid="rv-foto" />
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Piezas buenas que salieron: medida en milímetros (1400 × 3660) y cuántas de esa medida. Déjalo en blanco si se perdió toda.</p>
          {filas.map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input inputMode="decimal" placeholder="mm" value={f.alto} onChange={(e) => cambiar(i, 'alto', e.target.value)} className={`w-24 text-right ${noCabe(i) ? 'border-red-400' : ''}`} aria-label={`Primera medida de la pieza ${i + 1}`} data-testid={`rv-alto-${i}`} />
              <span className="text-muted-foreground">×</span>
              <Input inputMode="decimal" placeholder="mm" value={f.ancho} onChange={(e) => cambiar(i, 'ancho', e.target.value)} className={`w-24 text-right ${noCabe(i) ? 'border-red-400' : ''}`} aria-label={`Segunda medida de la pieza ${i + 1}`} data-testid={`rv-ancho-${i}`} />
              <Input inputMode="numeric" value={f.cantidad} onChange={(e) => cambiar(i, 'cantidad', e.target.value)} className="w-16 text-right" aria-label={`Cuántas piezas de esa medida (${i + 1})`} title="Cuántas de esa medida" data-testid={`rv-cant-${i}`} />
              <Input placeholder="Nota (opcional)" value={f.descripcion} onChange={(e) => cambiar(i, 'descripcion', e.target.value)} className="flex-1" aria-label={`Nota de la pieza ${i + 1}`} data-testid={`rv-desc-${i}`} />
              <Button type="button" size="sm" variant="ghost" disabled={filas.length === 1} onClick={() => setFilas((x) => x.filter((_, j) => j !== i))} aria-label={`Quitar la pieza ${i + 1}`}><Trash2 /></Button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" disabled={filas.length >= 40} onClick={() => setFilas((x) => [...x, { ...FILA_VACIA }])} data-testid="rv-agregar"><Plus /> Otra medida</Button>
          {revision ? (
            <p className={`rounded-lg px-3 py-2 text-xs ${corrige ? 'bg-red-50 text-red-800' : 'bg-muted/60 text-muted-foreground'}`} data-testid="rv-area">
              Las piezas ocupan {m2(revision.usado_m2)} de {m2(revision.lamina_m2)}.
              {revision.no_caben.length > 0 ? ' Hay piezas que no caben en la lámina ni giradas: corrige la medida.' : revision.excede ? ' Ocupan más que la lámina: corrige las medidas.' : ` El resto (${m2(revision.lamina_m2.sub(revision.usado_m2))}) queda como merma.`}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cancelar</Button>
          <Button onClick={guardar} disabled={corrige || !lamina} cargando={registrar.isPending} data-testid="rv-guardar">Enviar reporte</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportarDetalle({ almacenes, cerrar }: { almacenes: AlmacenDTO[]; cerrar: () => void }) {
  const [almacenId, setAlmacenId] = useState(() => almacenes.find((a) => a.principal)?.id ?? almacenes[0]?.id ?? '');
  const [q, setQ] = useState('');
  const [productoId, setProductoId] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [detalle, setDetalle] = useState('');
  const [fotos, setFotos] = useState<string[]>([]);
  const [pagina, setPagina] = usePaginaBusqueda(q);
  const productos = useProductos(q, pagina);
  const [elegido, setElegido] = useState<ProductoResumenDTO | null>(null);
  const registrar = useRegistrarProductoDetalle();
  const opciones = conElegido((productos.data?.datos ?? []).filter((p) => p.tipo === 'producto'), elegido);
  const guardar = () => {
    const r = productoDetalleInput.safeParse({ almacen_id: almacenId, producto_id: productoId, cantidad: cantidad.trim(), detalle, ...(fotos.length > 0 ? { fotos } : {}) });
    if (!r.success) {
      const campo = r.error.issues[0]?.path[0];
      toast.error(campo === 'producto_id' ? 'Elige el producto' : campo === 'cantidad' ? 'Indica cuántos son (unidades enteras)' : (r.error.issues[0]?.message ?? 'Revisa los datos'));
      return;
    }
    registrar.mutate(r.data, {
      onSuccess: (d) => {
        toast.success(`Reporte ${d.numero_formateado} enviado`, { description: 'Queda por aprobar: la dueña le pone el precio especial y pasa a «PRODUCTOS CON DETALLES».' });
        cerrar();
      },
      onError: (e) => toast.error('No se pudo reportar', { description: mensajeDeError(e) }),
    });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="dialogo-detalle">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Reportar producto con detalle</DialogTitle><AyudaVentana tema="roturas.producto-detalle" /></div>
          <DialogDescription>Tapa rota, vencido, golpeado… Indica cuál, cuántos y qué tiene, con la foto. Mientras la dueña no lo apruebe, se sigue vendiendo normal.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-3">
          <Campo etiqueta="Almacén" htmlFor="pd-almacen" className="col-span-3">
            <Select id="pd-almacen" value={almacenId} onChange={(e) => setAlmacenId(e.target.value)} data-testid="pd-almacen">
              {almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </Select>
          </Campo>
          <Campo etiqueta="Cuántos" htmlFor="pd-cantidad">
            <Input id="pd-cantidad" inputMode="numeric" value={cantidad} onChange={(e) => setCantidad(e.target.value)} className="text-right" data-testid="pd-cantidad" />
          </Campo>
          <Campo etiqueta="Producto" htmlFor="pd-producto" className="col-span-4">
            <div className="flex gap-2">
              <Input placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} className="w-40" data-testid="pd-producto-buscar" />
              <Select id="pd-producto" value={productoId} onChange={(e) => { setProductoId(e.target.value); setElegido(opciones.find((p) => p.id === e.target.value) ?? null); }} data-testid="pd-producto">
                <option value="">Elige el producto…</option>
                {opciones.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
              </Select>
            </div>
            <PaginadorBusqueda pagina={pagina} limite={LIMITE_REPORTE} total={productos.data?.total ?? 0} onPagina={setPagina} testid="pd-producto" className="mt-1 rounded-lg border" />
          </Campo>
        </div>
        <Campo etiqueta="Detalle" htmlFor="pd-detalle">
          <Input id="pd-detalle" placeholder="Tapa rota" value={detalle} onChange={(e) => setDetalle(e.target.value)} data-testid="pd-detalle" />
        </Campo>
        <FotosPrueba fotos={fotos} cambiar={setFotos} testid="pd-foto" />
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cancelar</Button>
          <Button onClick={guardar} cargando={registrar.isPending} data-testid="pd-guardar">Enviar reporte</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Revisión de la dueña ───────────────────────────────────────────────────
function RevisarReporte({ reporte, puedeAprobar, cerrar }: { reporte: DespieceVidrioDTO; puedeAprobar: boolean; cerrar: () => void }) {
  const foto = useFotoReporte(reporte.id, reporte.tiene_foto);
  const imagenes = foto.data?.imagenes ?? (foto.data?.imagen ? [foto.data.imagen] : []);
  /** La foto que se ve en grande (índice), o null. */
  const [fotoGrande, setFotoGrande] = useState<number | null>(null);
  const resolver = useResolverDespieceVidrio();
  const porAprobar = reporte.estado === 'por_aprobar' && puedeAprobar;
  const [armado, setArmado] = useState(() => Object.fromEntries(reporte.piezas.map((z) => [z.id, { nombre: z.nombre, cantidad: z.declarada, precio: '' }])));
  const [comentario, setComentario] = useState('');
  const cambiar = (id: string, campo: 'nombre' | 'cantidad' | 'precio', valor: string) => setArmado((a) => ({ ...a, [id]: { ...a[id]!, [campo]: valor } }));
  const esVidrio = reporte.clase === 'vidrio';
  const lote = reporte.piezas[0];
  const cantidadMal = (z: PiezaDTO) => !/^\d+$/.test(armado[z.id]!.cantidad.trim()) || Number(armado[z.id]!.cantidad) > Number(z.declarada);
  const merma = reporte.piezas.filter((z) => !normalizarDecimal(armado[z.id]!.precio) || Number(armado[z.id]!.cantidad) === 0).length;

  const aprobar = () => {
    if (esVidrio && reporte.piezas.some(cantidadMal)) {
      toast.error('Revisa las cantidades: enteras y a lo sumo las reportadas');
      return;
    }
    const piezas = reporte.piezas.map((z) => {
      const a = armado[z.id]!;
      return { pieza_id: z.id, precio_divisa: normalizarDecimal(a.precio) || null, ...(esVidrio ? { cantidad: a.cantidad.trim(), ...(a.nombre.trim() && a.nombre.trim() !== z.nombre ? { nombre: a.nombre.trim() } : {}) } : {}) };
    });
    resolver.mutate(
      { id: reporte.id, decision: 'aprobar', datos: { piezas, ...(comentario.trim() ? { comentario: comentario.trim() } : {}) } },
      {
        onSuccess: (d) => {
          toast.success(`Reporte ${d.numero_formateado} aprobado`, {
            description: esVidrio ? `Salió la lámina del inventario. ${d.piezas.filter((z) => z.estado === 'disponible').length} medida(s) pasaron a «${ARTICULO_PIEZAS_VIDRIO}».` : `${cant(reporte.cantidad)} unidad(es) salieron de la existencia normal y pasaron a «${ARTICULO_PRODUCTOS_DETALLE}».`,
          });
          cerrar();
        },
        onError: (e) => toast.error('No se pudo aprobar', { description: mensajeDeError(e) }),
      },
    );
  };
  const rechazar = () => {
    if (comentario.trim().length < 3) {
      toast.error('Escribe el motivo del rechazo');
      return;
    }
    resolver.mutate(
      { id: reporte.id, decision: 'rechazar', datos: { comentario: comentario.trim() } },
      { onSuccess: (d) => { toast.success(`Reporte ${d.numero_formateado} rechazado`, { description: 'No se movió el inventario.' }); cerrar(); }, onError: (e) => toast.error('No se pudo rechazar', { description: mensajeDeError(e) }) },
    );
  };
  const usado = esVidrio && reporte.lamina ? revisarPiezasEnLamina(reporte.piezas.map((z) => ({ alto_mm: z.alto_mm ?? '0', ancho_mm: z.ancho_mm ?? '0', cantidad: z.declarada })), reporte.lamina, reporte.cantidad) : null;

  return (
    <Dialog open onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="dialogo-revisar">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>{reporte.numero_formateado} · {reporte.producto_nombre}</DialogTitle><AyudaVentana tema="roturas.revisar" /></div>
          <DialogDescription>
            {esVidrio ? `Rotura de ${cant(reporte.cantidad)} lámina(s)` : `${cant(reporte.cantidad)} unidad(es) con detalle`} en {reporte.almacen_nombre} · reportó {reporte.creado_por_nombre} el {formatearFechaHora(reporte.creado_en)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-4">
          {reporte.tiene_foto ? (
            imagenes.length > 0 ? (
              // El navegador no abre una data URL en otra pestaña: la foto se agranda aquí mismo
              <div className="shrink-0 space-y-1.5">
                <button type="button" onClick={() => setFotoGrande(0)} title="Ver la foto grande">
                  <img src={imagenes[0]} alt="Foto de la prueba" className="h-40 w-40 rounded-xl border object-cover" data-testid="rev-foto" />
                </button>
                {imagenes.length > 1 ? (
                  <div className="flex w-40 flex-wrap gap-1" data-testid="rev-fotos">
                    {imagenes.slice(1).map((im, i) => (
                      <button key={i} type="button" onClick={() => setFotoGrande(i + 1)} title={`Ver la foto ${i + 2} grande`}>
                        <img src={im} alt={`Foto ${i + 2} de la prueba`} className="h-[37px] w-[37px] rounded-md border object-cover" data-testid="rev-foto-mini" />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex h-40 w-40 shrink-0 items-center justify-center rounded-xl border text-xs text-muted-foreground">Cargando la foto…</div>
            )
          ) : (
            <div className="flex h-40 w-40 shrink-0 items-center justify-center rounded-xl border border-dashed text-center text-xs text-muted-foreground" data-testid="rev-sin-foto">Sin foto</div>
          )}
          <div className="min-w-0 flex-1 space-y-2 text-sm">
            <p className="rounded-xl bg-muted/60 px-3 py-2"><span className="text-xs text-muted-foreground">{esVidrio ? 'Qué pasó: ' : 'Detalle: '}</span>{reporte.motivo}</p>
            {usado ? (
              <p className="text-xs text-muted-foreground" data-testid="rev-area">
                Lámina de {medidaMm(String(reporte.lamina!.largo_mm), String(reporte.lamina!.ancho_mm))} mm: las piezas reportadas ocupan {m2(usado.usado_m2)} de {m2(usado.lamina_m2)}; el resto es merma.
              </p>
            ) : null}
            {!esVidrio && lote ? (
              <p className="text-xs text-muted-foreground">Precio normal del producto: {lote.precio_normal ? usd(lote.precio_normal) : 'sin precio en la lista por defecto'}. Las unidades aprobadas salen de la existencia normal.</p>
            ) : null}
          </div>
        </div>
        {fotoGrande !== null && imagenes[fotoGrande] ? (
          <div className="space-y-1">
            <button type="button" className="block w-full" onClick={() => setFotoGrande(null)} title="Volver" data-testid="rev-foto-grande">
              <img src={imagenes[fotoGrande]} alt={`Foto ${fotoGrande + 1} de la prueba, grande`} className="max-h-[60vh] w-full rounded-xl border object-contain" />
            </button>
            {imagenes.length > 1 ? (
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Button type="button" size="sm" variant="ghost" disabled={fotoGrande === 0} onClick={() => setFotoGrande(fotoGrande - 1)} data-testid="rev-foto-anterior">Anterior</Button>
                <span data-testid="rev-foto-posicion">Foto {fotoGrande + 1} de {imagenes.length}</span>
                <Button type="button" size="sm" variant="ghost" disabled={fotoGrande === imagenes.length - 1} onClick={() => setFotoGrande(fotoGrande + 1)} data-testid="rev-foto-siguiente">Siguiente</Button>
              </div>
            ) : null}
          </div>
        ) : null}
        {esVidrio && reporte.piezas.length === 0 ? <p className="text-sm text-muted-foreground">No salió ninguna pieza buena: toda la lámina es merma.</p> : null}
        {reporte.piezas.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                {esVidrio ? <TableHead>Medida</TableHead> : <TableHead>Producto</TableHead>}
                <TableHead className="text-right">Reportadas</TableHead>
                {esVidrio ? <TableHead>Nombre para la venta</TableHead> : null}
                {esVidrio ? <TableHead className="text-right">Cantidad</TableHead> : null}
                <TableHead className="text-right">{porAprobar ? (esVidrio ? 'Precio c/u $' : 'Precio especial $') : 'Precio'}</TableHead>
                {porAprobar ? null : <TableHead>Estado</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {reporte.piezas.map((z) => (
                <TableRow key={z.id}>
                  <TableCell>
                    <p className="font-medium">{esVidrio && z.alto_mm && z.ancho_mm ? medidaMm(z.alto_mm, z.ancho_mm) : z.nombre}</p>
                    <p className="text-[11px] text-muted-foreground">{z.codigo}{z.descripcion ? ` · ${z.descripcion}` : ''}</p>
                  </TableCell>
                  <TableCell className="num text-right">{z.declarada}</TableCell>
                  {esVidrio ? (
                    <TableCell>
                      {porAprobar ? <Input value={armado[z.id]!.nombre} onChange={(e) => cambiar(z.id, 'nombre', e.target.value)} className="h-8 min-w-56" aria-label={`Nombre de ${z.codigo}`} data-testid={`rev-nombre-${z.codigo}`} /> : z.nombre}
                    </TableCell>
                  ) : null}
                  {esVidrio ? (
                    <TableCell className="num text-right">
                      {porAprobar ? <Input inputMode="numeric" value={armado[z.id]!.cantidad} onChange={(e) => cambiar(z.id, 'cantidad', e.target.value)} className={`ml-auto h-8 w-16 text-right ${cantidadMal(z) ? 'border-red-400' : ''}`} aria-label={`Cantidad de ${z.codigo}`} data-testid={`rev-cant-${z.codigo}`} /> : z.cantidad}
                    </TableCell>
                  ) : null}
                  <TableCell className="num text-right">
                    {porAprobar ? (
                      <Input inputMode="decimal" placeholder={esVidrio ? 'merma' : '$'} value={armado[z.id]!.precio} onChange={(e) => cambiar(z.id, 'precio', e.target.value)} className="ml-auto h-8 w-24 text-right" aria-label={`Precio de ${z.codigo}`} data-testid={`rev-precio-${z.codigo}`} />
                    ) : z.precio_divisa ? (
                      usd(z.precio_divisa)
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  {porAprobar ? null : <TableCell><Badge variant={ESTADO_PIEZA[z.estado].variante}>{ESTADO_PIEZA[z.estado].texto}</Badge></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
        {porAprobar ? (
          <>
            <p className="text-xs text-muted-foreground" data-testid="rev-aviso">
              {esVidrio
                ? `Al aprobar sale la lámina del inventario y las piezas con precio pasan a «${ARTICULO_PIEZAS_VIDRIO}». La medida sin precio (o con cantidad 0) queda como merma${merma > 0 && reporte.piezas.length > 0 ? ` (${merma} sin precio)` : ''}.`
                : `Al aprobar, ${cant(reporte.cantidad)} unidad(es) salen de la existencia normal y pasan a «${ARTICULO_PRODUCTOS_DETALLE}» con el precio especial. Si no se va a vender, recházalo.`}
            </p>
            <Campo etiqueta="Comentario (obligatorio para rechazar)" htmlFor="rev-comentario">
              <Textarea id="rev-comentario" rows={2} value={comentario} onChange={(e) => setComentario(e.target.value)} data-testid="rev-comentario" />
            </Campo>
          </>
        ) : reporte.estado !== 'por_aprobar' ? (
          <p className="text-xs text-muted-foreground">
            {ESTADO_REPORTE[reporte.estado].texto} por {reporte.resuelto_por_nombre ?? '—'}{reporte.resuelto_en ? ` el ${formatearFechaHora(reporte.resuelto_en)}` : ''}{reporte.comentario ? ` · ${reporte.comentario}` : ''}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">Pendiente: lo aprueba y le pone el precio la dueña.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cerrar</Button>
          {porAprobar ? (
            <>
              <Button variant="destructive" onClick={rechazar} disabled={resolver.isPending} data-testid="rev-rechazar">Rechazar</Button>
              <Button onClick={aprobar} cargando={resolver.isPending} data-testid="rev-aprobar">Aprobar</Button>
            </>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Lo que está en las listas del punto de venta ───────────────────────────
function Lotes({ clase }: { clase: 'vidrio' | 'detalle' }) {
  const { puede } = useAuth();
  const puedeDecidir = puede('inventario.ajustes');
  const puedePrecio = puede('productos.precios');
  const [estado, setEstado] = useState<EstadoPieza | ''>('disponible');
  const lotes = usePiezas({ clase, estado: estado || undefined });
  const [precio, setPrecio] = useState<PiezaDTO | null>(null);
  const [saliendo, setSaliendo] = useState<{ lote: PiezaDTO; tipo: TipoSalidaPieza } | null>(null);
  const esVidrio = clase === 'vidrio';
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <p className="pb-2 text-sm text-muted-foreground">
          En el punto de venta: busca «{esVidrio ? ARTICULO_PIEZAS_VIDRIO : ARTICULO_PRODUCTOS_DETALLE}». Al agotarse sale de la lista y queda aquí, en el historial.
        </p>
        <Filtro etiqueta="Mostrar">
          <Select value={estado} onChange={(e) => setEstado(e.target.value as EstadoPieza | '')} className="w-52" aria-label={esVidrio ? 'Qué piezas de vidrio mostrar' : 'Qué lotes mostrar'} data-testid="piezas-estado">
            <option value="disponible">En la lista (se venden)</option>
            <option value="agotada">Agotadas</option>
            <option value="por_aprobar">Por aprobar</option>
            {esVidrio ? <option value="merma">Merma</option> : null}
            <option value="">Todas (historial)</option>
          </Select>
        </Filtro>
      </div>
      <EstadoQuery cargando={lotes.isLoading} error={lotes.error} datos={lotes.data} reintentar={() => void lotes.refetch()}>
        {(d) =>
          d.length === 0 ? (
            <p className="rounded-xl border border-dashed py-6 text-center text-sm text-muted-foreground" data-testid="piezas-vacio">Nada en este estado.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{esVidrio ? 'Pieza' : 'Producto'}</TableHead>
                  {esVidrio ? <TableHead>Medida</TableHead> : <TableHead>Detalle</TableHead>}
                  <TableHead className="text-right">Quedan</TableHead>
                  <TableHead className="text-right">Vendidas</TableHead>
                  <TableHead className="text-right">{esVidrio ? 'Precio c/u' : 'Precio especial'}</TableHead>
                  <TableHead>Almacén</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.map((z) => (
                  <TableRow key={z.id} data-testid={`pieza-${z.codigo}`}>
                    <TableCell>
                      <p className="font-medium">{z.nombre}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {esVidrio ? z.codigo : z.producto_codigo} · {z.despiece_numero}
                        {z.salidas.length > 0 ? ` · ${z.salidas.map((s) => `${TIPO_SALIDA_PIEZA_ETIQUETA[s.tipo].toLowerCase()} ${s.cantidad}: ${s.motivo}`).join('; ')}` : ''}
                      </p>
                    </TableCell>
                    <TableCell className={esVidrio ? 'num' : 'text-sm'}>{esVidrio ? (z.alto_mm && z.ancho_mm ? medidaMm(z.alto_mm, z.ancho_mm) : '—') : (z.descripcion ?? '—')}</TableCell>
                    <TableCell className="num text-right font-semibold">{z.disponible} <span className="text-[11px] font-normal text-muted-foreground">de {z.cantidad}</span></TableCell>
                    <TableCell className="num text-right">{z.vendidas}</TableCell>
                    <TableCell className="num text-right">
                      <span className="font-semibold">{z.precio_divisa ? usd(z.precio_divisa) : '—'}</span>
                      {!esVidrio && z.precio_normal ? <span className="block text-[11px] text-muted-foreground">normal {usd(z.precio_normal)}</span> : null}
                    </TableCell>
                    <TableCell>{z.almacen_nombre}</TableCell>
                    <TableCell><Badge variant={ESTADO_PIEZA[z.estado].variante}>{ESTADO_PIEZA[z.estado].texto}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      {z.estado === 'disponible' && puedePrecio ? <Button size="sm" variant="ghost" onClick={() => setPrecio(z)} data-testid={`pieza-precio-${z.codigo}`}>Precio</Button> : null}
                      {z.estado === 'disponible' && puedeDecidir ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setSaliendo({ lote: z, tipo: 'baja' })} data-testid={`pieza-baja-${z.codigo}`}>Dar de baja</Button>
                          <Button size="sm" variant="ghost" onClick={() => setSaliendo({ lote: z, tipo: 'uso_interno' })} data-testid={`pieza-uso-${z.codigo}`}>Usar en la tienda</Button>
                          {esVidrio ? null : <Button size="sm" variant="ghost" onClick={() => setSaliendo({ lote: z, tipo: 'reintegro' })} data-testid={`pieza-reintegro-${z.codigo}`}>Volver a la existencia</Button>}
                        </>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </EstadoQuery>
      {precio ? <PrecioDialog key={precio.id} lote={precio} cerrar={() => setPrecio(null)} /> : null}
      {saliendo ? <SalidaDialog key={`${saliendo.lote.id}-${saliendo.tipo}`} lote={saliendo.lote} tipoInicial={saliendo.tipo} cerrar={() => setSaliendo(null)} /> : null}
    </section>
  );
}

function PrecioDialog({ lote, cerrar }: { lote: PiezaDTO; cerrar: () => void }) {
  const [precio, setPrecio] = useState(() => (lote.precio_divisa ? Money.parse(lote.precio_divisa).toDisplay() : ''));
  const editar = useEditarPieza();
  return (
    <Dialog open onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-md" data-testid="dialogo-precio">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Precio de {lote.nombre}</DialogTitle><AyudaVentana tema="roturas.precio" /></div>
          <DialogDescription>Quedan {lote.disponible}. {lote.clase === 'detalle' && lote.precio_normal ? `El precio normal es ${usd(lote.precio_normal)}.` : ''}</DialogDescription>
        </DialogHeader>
        <Campo etiqueta="Precio de venta $ (cada uno)" htmlFor="pz-precio">
          <Input id="pz-precio" inputMode="decimal" value={precio} onChange={(e) => setPrecio(e.target.value)} className="text-right" autoFocus data-testid="pz-precio" />
        </Campo>
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cancelar</Button>
          <Button
            disabled={!normalizarDecimal(precio)}
            cargando={editar.isPending}
            data-testid="pz-guardar"
            onClick={() =>
              editar.mutate({ id: lote.id, precio_divisa: normalizarDecimal(precio) }, { onSuccess: () => { toast.success('Precio actualizado'); cerrar(); }, onError: (e) => toast.error('No se pudo guardar', { description: mensajeDeError(e) }) })
            }
          >
            Guardar precio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SalidaDialog({ lote, tipoInicial, cerrar }: { lote: PiezaDTO; tipoInicial: TipoSalidaPieza; cerrar: () => void }) {
  const [tipo, setTipo] = useState<TipoSalidaPieza>(tipoInicial);
  const [cantidad, setCantidad] = useState(lote.disponible);
  const [motivo, setMotivo] = useState('');
  const salida = useSalidaPieza();
  const tipos: TipoSalidaPieza[] = lote.clase === 'vidrio' ? ['baja', 'uso_interno'] : ['baja', 'uso_interno', 'reintegro'];
  const ayuda: Record<TipoSalidaPieza, string> = {
    baja: 'Ya no sirve: sale de la lista como pérdida.',
    uso_interno: 'Se usa en la tienda (por ejemplo, pintar la oficina): sale de la lista.',
    reintegro: 'Se puede vender normal: vuelve a la existencia del producto, a su precio normal.',
  };
  const cantidadBien = /^\d+$/.test(cantidad.trim()) && Number(cantidad) >= 1 && Number(cantidad) <= Number(lote.disponible);
  return (
    <Dialog open onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-md" data-testid="dialogo-salida">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>{lote.nombre}</DialogTitle><AyudaVentana tema="roturas.salida" /></div>
          <DialogDescription>Quedan {lote.disponible}{lote.descripcion ? ` · ${lote.descripcion}` : ''}. Tú decides qué pasa con lo que no se vende, siempre con el motivo.</DialogDescription>
        </DialogHeader>
        <Campo etiqueta="Qué pasa" htmlFor="sal-tipo" ayuda={ayuda[tipo]}>
          <Select id="sal-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoSalidaPieza)} data-testid="sal-tipo">
            {tipos.map((t) => <option key={t} value={t}>{TIPO_SALIDA_PIEZA_ETIQUETA[t]}</option>)}
          </Select>
        </Campo>
        <div className="grid grid-cols-3 gap-3">
          <Campo etiqueta="Cuántos" htmlFor="sal-cantidad">
            <Input id="sal-cantidad" inputMode="numeric" value={cantidad} onChange={(e) => setCantidad(e.target.value)} className={`text-right ${cantidadBien ? '' : 'border-red-400'}`} data-testid="sal-cantidad" />
          </Campo>
          <Campo etiqueta="Motivo" htmlFor="sal-motivo" className="col-span-2">
            <Input id="sal-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={tipo === 'uso_interno' ? 'Pintar la oficina' : tipo === 'reintegro' ? 'La tapa se cambió' : 'Vencido'} autoFocus data-testid="sal-motivo" />
          </Campo>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cancelar</Button>
          <Button
            variant={tipo === 'baja' ? 'destructive' : 'default'}
            disabled={!cantidadBien || motivo.trim().length < 3}
            cargando={salida.isPending}
            data-testid="sal-guardar"
            onClick={() =>
              salida.mutate(
                { id: lote.id, datos: { tipo, cantidad: cantidad.trim(), motivo: motivo.trim() } },
                { onSuccess: (z) => { toast.success(`${TIPO_SALIDA_PIEZA_ETIQUETA[tipo]}: ${cantidad} de ${lote.nombre}`, { description: z.estado === 'agotada' ? 'Ya no quedan: salió de la lista.' : `Quedan ${z.disponible}.` }); cerrar(); }, onError: (e) => toast.error('No se pudo registrar', { description: mensajeDeError(e) }) },
              )
            }
          >
            {TIPO_SALIDA_PIEZA_ETIQUETA[tipo]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
